import React, { createContext, useContext, useState, useEffect } from 'react'

// Define the Chromecast context type
interface ChromecastContextType {
  isCastAvailable: boolean
  castContext: any
  isInitialized: boolean
  isReady: boolean // New flag to indicate the context is fully ready
}

// Create the context with default values
const ChromecastContext = createContext<ChromecastContextType>({
  isCastAvailable: false,
  castContext: null,
  isInitialized: false,
  isReady: false,
})

// Provider component for Chromecast context
export const ChromecastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isCastAvailable, setIsCastAvailable] = useState(false)
  const [castContext, setCastContext] = useState<any>(null)
  const [isInitialized, setIsInitialized] = useState(false)
  const [isReady, setIsReady] = useState(false)

  useEffect(() => {
    let initializationTimeout: NodeJS.Timeout | null = null

    // Function to initialize Cast API
    const initializeCastApi = () => {
      try {
        if ((window as any).cast?.framework?.CastContext) {
          const context = (window as any).cast.framework.CastContext.getInstance()

          // Check if context is valid before setting options
          if (context && typeof context.setOptions === 'function') {
            context.setOptions({
              receiverApplicationId:
                window.location.hostname === 'localhost'
                  ? 'CC1AD845' // Default receiver app ID for local testing
                  : '73CDCD18', // Your production app ID
              autoJoinPolicy: (window as any).chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED,
            })
            console.log('Cast API initialized successfully')
            setCastContext(context)
            setIsInitialized(true)

            // Add a small delay to ensure everything is properly initialized
            initializationTimeout = setTimeout(() => {
              setIsReady(true)
              console.log('Chromecast context is now fully ready')
            }, 500)
          } else {
            console.error('Cast context is not valid or setOptions is not a function')
          }
        } else {
          console.error('Cast framework or CastContext is not available')
        }
      } catch (error) {
        console.error('Error initializing Cast API:', error)
      }
    }

    // Check if Cast API is already available
    if ((window as any).chrome?.cast?.isAvailable) {
      console.log('Cast API is available')
      setIsCastAvailable(true)
      initializeCastApi()
    } else {
      // Set up callback for when Cast API becomes available
      if (!(window as any).__onGCastApiAvailable) {
        ;(window as any).__onGCastApiAvailable = (available: boolean) => {
          console.log('Cast API available callback:', available)
          setIsCastAvailable(available)
          if (available) {
            // Add a small delay to ensure the API is fully loaded
            setTimeout(() => {
              initializeCastApi()
            }, 100)
          }
        }
      }
    }

    // Cleanup function
    return () => {
      if (initializationTimeout) {
        clearTimeout(initializationTimeout)
      }
    }
  }, [])

  return (
    <ChromecastContext.Provider
      value={{
        isCastAvailable,
        castContext,
        isInitialized,
        isReady,
      }}
    >
      {children}
    </ChromecastContext.Provider>
  )
}

// Custom hook to use the Chromecast context
export const useChromecast = () => useContext(ChromecastContext)

export default ChromecastContext
